import { useState, useCallback } from "react";
import { Grid, Box, Alert } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useAuthentication } from "hooks";
import { SIGN_IN_ERROR_MESSAGES } from "constants";
import { BasePage, LoadingButton, LinkButton, EmailInput, PasswordInput } from "components";

export function SignInPage() {
  const { isButtonLoading, signIn } = useAuthentication();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const returnUrl = location.state?.returnUrl || urlParams.get('returnUrl');

  const [values, setValues] = useState({ email: "", password: "" });
  const [submitError, setSubmitError] = useState("");
  const [validation, setValidation] = useState({ email: false, password: false });

  const handleEmailChange = (email) => {
    if (submitError) setSubmitError("");
    setValues((v) => ({ ...v, email }));
  };

  const handlePasswordChange = (password) => {
    if (submitError) setSubmitError("");
    setValues((v) => ({ ...v, password }));
  };

  const clearPassword = useCallback(() => {
    setValues((v) => ({ ...v, password: "" }));
  }, []);

  const handleEmailValidation = useCallback((isValid) => {
    setValidation((v) => ({ ...v, email: isValid }));
  }, []);

  const handlePasswordValidation = useCallback((isValid) => {
    setValidation((v) => ({ ...v, password: isValid }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = values.email.trim();
    const password = values.password.trim();

    if (!email || !password) return;

    try {
      await signIn(email, password);
      clearPassword();
    } catch (err) {
      clearPassword();
      console.error("Sign in error:", err);
      const message =
        (err && err.code && SIGN_IN_ERROR_MESSAGES[err.code]) ||
        "An unexpected error occurred. Please try again.";
      setSubmitError(message);
    }
  };

  const isFormValid = validation.email && validation.password;

  return (
    <BasePage title="Sign in">
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid size={12}>
            <EmailInput
              value={values.email}
              onChange={handleEmailChange}
              onValidationChange={handleEmailValidation}
            />
          </Grid>

          <Grid size={12}>
            <PasswordInput
              value={values.password}
              onChange={handlePasswordChange}
              onValidationChange={handlePasswordValidation}
            />
          </Grid>

          {submitError && (
            <Grid size={12}>
              <Alert icon={<Clear fontSize="inherit" />} severity="error">
                {submitError}
              </Alert>
            </Grid>
          )}

          <Grid size={12}>
            <LoadingButton
              type="submit"
              loading={isButtonLoading}
              disabled={!isFormValid}
            >
              Sign in
            </LoadingButton>
          </Grid>

          <Grid size={12} className="form-actions">
            <LinkButton to="/forgot-password">Forgot password?</LinkButton>
            <LinkButton to="/sign-up" state={returnUrl ? { returnUrl } : undefined}>Don&apos;t have an account? Sign up</LinkButton>
          </Grid>
        </Grid>
      </Box>
    </BasePage>
  );
}